import {
  Box,
  Flex,
  SimpleGrid,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Stack,
  Text,
  useBreakpointValue,
  HStack,
  IconButton,
  Tooltip
} from "@chakra-ui/react";
import { useCallback, useState, useEffect } from "react";
import { RiSearchLine, RiGridLine, RiCalendarLine } from "react-icons/ri";
import { Select as ChakraReactSelect } from "chakra-react-select";

import { Header } from "../../components/Header";
import { Sidebar } from "../../components/Sidebar";
import { ClassCard } from "../../components/Card"
import { ButtonDefault } from "../../components/ButtonDefault";
import { api } from "../../services/api";
import { Event, useClassesList } from "../../services/hooks/useClassesList";
import ContentBase from "../../components/ContentBase";
import MainBase from "../../components/MainBase";
import MainActions from "../../components/MainActions";
import { RemoveAccent } from "../../utils/RemoveAccent";
import { WeekCalendarView } from "../../components/WeekCalendar";
import { ViewMode } from "../../types/Calendar";

type LevelData = {
  id: string;
  name: string;
}

type SelectInputProps = {
  label: string;
  value: string;
}

export function ClassesList() {
  const [events, setEvents] = useState<Event[]>([]);
  const [search, setSearch] = useState('');
  const [levelsData, setLevelsData] = useState<LevelData[]>([]);
  const [selectedLevel, setSelectedLevel] = useState<SelectInputProps>({
    label: "Nível - Todos",
    value: ""
  });
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [numberOfStudentsOption, setNumberOfStudentsOption] = useState({
    all: false,
    individual: true,
    group: false
  });

  const { data, isLoading, error } = useClassesList();

  useEffect(() => {
    if (data) {
      setEvents(data)
    }
  }, [data])

  useEffect(() => {
    api.get("/levels").then(response => {
      const levels = [
        {
          id: "",
          name: "Nível - Todos",
        },
        ...response.data
      ]

      setLevelsData(levels)
    })
  }, [])

  useEffect(() => {
    const savedLevel = localStorage.getItem("selectedLevel");
    if (savedLevel) {
      try {
        const parsedLevel = JSON.parse(savedLevel);
        setSelectedLevel(parsedLevel);
      } catch (error) {
        console.log("Erro ao recuperar nível do localStorage", error);
      }
    }
  }, [])

  const handleChangeSearch = useCallback((event) => {
    setSearch(event.target.value)
  }, [])

  const matchesSearchTerm = useCallback((event: Event, searchTerm: string) => {
    if (!searchTerm) return true;
    
    const searchWithoutAccent = RemoveAccent(searchTerm.toLowerCase());
    const titleMatches = RemoveAccent(event.title.toLowerCase()).includes(searchWithoutAccent);
    const descriptionMatches = RemoveAccent(event.description.toLowerCase()).includes(searchWithoutAccent);
    
    return titleMatches || descriptionMatches;
  }, []);

  const matchesLevel = useCallback((event: Event, selectedLevelId: string) => {
    if (!selectedLevelId) return true;

    // Se o evento for individual e filtro individual estiver ativo, retorna true
    // Se o evento for individual e filtro todos estiver ativo, retorna true
    const eventIsIndividual = event.student_limit === '1';
    if (eventIsIndividual && (numberOfStudentsOption.individual || numberOfStudentsOption.all)) {
      return true;
    }
    
    const hasSelectedLevel = event.levels.some(
      eventLevel => eventLevel.level_id === selectedLevelId
    );
    return hasSelectedLevel;
  }, [numberOfStudentsOption.individual, numberOfStudentsOption.all]);

  const matchesStudentLimit = useCallback((event: Event, options: typeof numberOfStudentsOption) => {
    if (options.all) return true;
    
    const studentLimit = Number(event.student_limit);
    
    if (options.individual) return studentLimit === 1;
    if (options.group) return studentLimit > 1;
    
    return false;
  }, []);

  const handleSearch = useCallback(() => {
    if (data) {
      const eventsFiltered = data.filter(event => {
        const searchMatches = matchesSearchTerm(event, search);
        const studentLimitMatches = matchesStudentLimit(event, numberOfStudentsOption);
        const levelMatches = matchesLevel(event, selectedLevel.value);

        return searchMatches && levelMatches && studentLimitMatches;
      });
      setEvents(eventsFiltered);
    }
  }, [search, data, numberOfStudentsOption, selectedLevel, matchesSearchTerm, matchesLevel, matchesStudentLimit])

  const handleOnPressEnter = useCallback((event) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  }, [handleSearch])

  const handleNumberOfStudentsOptionAndSearch = useCallback((option: 'all' | 'individual' | 'group') => {
    setNumberOfStudentsOption({
      all: option === 'all',
      individual: option === 'individual',
      group: option === 'group'
    });
  }, []);

  // Executa handleSearch quando data, selectedLevel e numberOfStudentsOption estiverem prontos
  useEffect(() => {
    if (data && selectedLevel) {
      handleSearch();
    }
  }, [data, selectedLevel, numberOfStudentsOption, handleSearch])

  const handleViewModeToggle = useCallback(() => {
    setViewMode(prev => prev === 'grid' ? 'week' : 'grid');
  }, []);

  const isWideVersion = useBreakpointValue({
    base: false,
    lg: true,
  })

  return (
    <Flex direction="column" h="100vh">
      { !isWideVersion && <Header /> }
      <ContentBase>
        <Sidebar />

        <MainBase
          mainTitle="Aulas disponíveis"
          isLoading={isLoading}
          overflow={"auto"}
        >
          <Stack
            w="100%"
            direction={["column", null, null, null, "row"]}
            alignItems="center"
            spacing="2"
          >
            <HStack spacing="2" flex="1" w="100%">
              <ButtonDefault
                w={["100%", null, null, null, "10%"]}
                text="Todas"
                onClick={() => handleNumberOfStudentsOptionAndSearch('all')}
                variant='outline'
                borderRadius={50}
                selected={numberOfStudentsOption.all}
              />
              <ButtonDefault
                w={["100%", null, null, null, "10%"]}
                text="Individuais"
                onClick={() => handleNumberOfStudentsOptionAndSearch('individual')}
                variant='outline'
                borderRadius={50}
                selected={numberOfStudentsOption.individual}
              />
              <ButtonDefault
                w={["100%", null, null, null, "10%"]}
                text="Em grupo"
                onClick={() => handleNumberOfStudentsOptionAndSearch('group')}
                variant='outline'
                borderRadius={50}
                selected={numberOfStudentsOption.group}
              />
              <Tooltip label={viewMode === 'week' ? 'Visualização em Grade' : 'Visualização em Calendário'}>
                <IconButton
                  aria-label={viewMode === 'week' ? 'Visualização em Grade' : 'Visualização em Calendário'}
                  icon={viewMode === 'week' ? <RiGridLine /> : <RiCalendarLine />}
                  onClick={handleViewModeToggle}
                  variant={viewMode === 'week' ? 'outline' : 'solid'}
                  colorScheme="blue"
                  size="md"
                />
              </Tooltip>
            </HStack>
          </Stack>

          <MainActions>
            <Stack
              w="100%"
              direction={["column", null, null, null, "row"]}
              alignItems="center"
              spacing="2"
            >
              <InputGroup
                w={["100%", null, null, null, "70%"]}
                bg="white"
              >
                <InputLeftElement
                  pointerEvents="none"
                  children={<RiSearchLine fill="var(--chakra-colors-blue-900)" />}
                />
                <Input
                  name="filter"
                  type="text"
                  color="blue.900"
                  borderColor="blue.900"
                  placeholder="Procurar..."
                  focusBorderColor="blue.500"
                  onChange={handleChangeSearch}
                  onKeyPress={handleOnPressEnter}
                  _hover={{
                    borderColor: "blue.500"
                  }}
                />
              </InputGroup>

              <Box
                w={["100%", null, null, null, "20%"]}
                mr={["0", null, null, "2"]}
                color="blue.900"
                bg="white"
              >
                <ChakraReactSelect
                  name="levels"
                  value={selectedLevel}
                  options={
                    levelsData.map(level => {
                      return (
                        { value: level.id, label: level.name }
                      )
                    })
                  }
                  placeholder="Selecione o nível ..."
                  closeMenuOnSelect={true}
                  onChange={(newValue: SelectInputProps) => {
                    if (newValue) {
                      setSelectedLevel(newValue as SelectInputProps);
                      localStorage.setItem("selectedLevel", JSON.stringify(newValue));
                    }
                  }}
                />
              </Box>

              <ButtonDefault
                w={["100%", null, null, null, "10%"]}
                text="Procurar"
                onClick={handleSearch}
              />
            </Stack>
          </MainActions>

          {/* Área de Conteúdo - Renderização Condicional */}
          {viewMode === 'week' ? (
            // Visualização em Calendário Semanal
            <WeekCalendarView
              events={data || []}
              filteredEvents={events}
              isLoading={isLoading}
            />
          ) : (
            // Visualização em Grid (original)
            <Box
              h={["", "80vh"]}
              overflow="auto"
              paddingRight="1"
              css={{
                '&::-webkit-scrollbar': {
                  width: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: "var(--chakra-colors-gray-400)",
                  borderRadius: '25px',
                },
              }}
            >
              { isLoading ? (
                <Flex justify="center">
                  <Spinner color="blue.900" size="xl" />
                </Flex>
              ) : error ? (
                <Flex direction="column">
                  <Text fontSize="lg" color="blue.900">Oops...ocorreu uma falha ao obter as aulas disponíveis :(</Text>
                  <Text fontSize="lg" color="blue.900">Tente novamente ;)</Text>
                </Flex>
              ) : (
                <SimpleGrid flex="1" columns={[1,null,2,2,3,4]} gap="2">
                  {events.map(event => (
                    <ClassCard
                      key={event.id}
                      eventId={event.id}
                      waiting={event.waiting}
                      title={event.title}
                      levels={event.levels}
                      description={event.description}
                      date={event.startDateFormated}
                      startHour={event.startHourFormated}
                      duration={event.duration}
                      credit={event.credit}
                      eventHasSubject={event.request_subject}
                      startDateISO={event.start_date}
                      dayOfWeek={event.dayOfWeek}
                      setHighlight={event.has_highlight}
                      forTeachers={event.for_teachers}
                      descriptionFormatted={event.description_formatted || event.description}
                      classSubject={event.class_subject_id}
                    />
                  ))}
                </SimpleGrid>
              )}
            </Box>
          )}
        </MainBase>
      </ContentBase>
    </Flex>
  )
}
